import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon,
  TagIcon,
  ChatBubbleLeftRightIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useCustomerDetail, useUpdateCustomer } from '../hooks/useCustomerList';
import NewConversationModal from '../../conversations/components/NewConversationModal';
import { CallCustomerButton } from '../../voice';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores';

interface Props {
  customerId: string;
  onClose: () => void;
}

export default function CustomerDetailModal({ customerId, onClose }: Props) {
  const { data: customer, isLoading } = useCustomerDetail(customerId);
  const updateMutation = useUpdateCustomer();
  const currency = useAuthStore((s) => s.currency);
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState('');

  useEffect(() => {
    if (customer) {
      setNotes(customer.notes || '');
      setTags(customer.tags.join(', '));
    }
  }, [customer]);

  const handleSave = () => {
    if (!customer) return;
    const tagList = tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    updateMutation.mutate(
      { id: customer.id, data: { notes, tags: tagList } },
      { onSuccess: () => setEditing(false) }
    );
  };

  const displayName = customer?.name || customer?.customerName || customer?.phoneNumber || 'Customer';

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="relative mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-panel bg-surface border border-line shadow-l"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-surface px-6 py-4">
          <h2 className="text-lg font-semibold text-ink">Customer Details</h2>
          <button onClick={onClose} className="rounded-xl p-1 hover:bg-black/[0.04]">
            <XMarkIcon className="h-5 w-5 text-ink-3" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
          </div>
        ) : customer ? (
          <div className="p-6 space-y-6">
            {/* Profile Header */}
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand/10 text-xl font-semibold text-brand">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-xl font-semibold text-ink">{displayName}</h3>
                <div className="mt-1 flex flex-wrap gap-3 text-sm text-ink-2">
                  {customer.phoneNumber && (
                    <span className="flex items-center gap-1">
                      <PhoneIcon className="h-4 w-4" />
                      {customer.phoneNumber}
                    </span>
                  )}
                  {customer.email && (
                    <span className="flex items-center gap-1">
                      <EnvelopeIcon className="h-4 w-4" />
                      {customer.email}
                    </span>
                  )}
                </div>
                {customer.firstContactDate && (
                  <p className="mt-1 text-xs text-ink-3">
                    Customer since {formatDate(customer.firstContactDate)}
                  </p>
                )}
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <MetricCard
                icon={<ShoppingBagIcon className="h-5 w-5 text-brand" />}
                label="Orders"
                value={String(customer.totalOrders)}
              />
              <MetricCard
                icon={<CurrencyDollarIcon className="h-5 w-5 text-brand" />}
                label="Lifetime Value"
                value={formatCurrency(customer.lifetimeValue, currency)}
              />
              <MetricCard
                icon={<ChatBubbleLeftRightIcon className="h-5 w-5 text-brand" />}
                label="Conversations"
                value={String(customer.totalConversations)}
              />
              <MetricCard
                icon={<ExclamationTriangleIcon className="h-5 w-5 text-warning" />}
                label="Escalations"
                value={String(customer.escalationCount)}
              />
            </div>

            {/* Tags */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <TagIcon className="h-4 w-4 text-ink-2" />
                <span className="text-sm font-medium text-ink-2">Tags</span>
              </div>
              {editing ? (
                <input
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="Comma-separated tags"
                  className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
                />
              ) : customer.tags.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {customer.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-full bg-surface-2 px-2.5 py-0.5 text-xs font-medium text-ink-2"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-ink-3">No tags</p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="mb-2 block text-sm font-medium text-ink-2">Notes</label>
              {editing ? (
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
                  placeholder="Add notes about this customer..."
                />
              ) : (
                <p className="text-sm text-ink-2 whitespace-pre-wrap">
                  {customer.notes || 'No notes'}
                </p>
              )}
            </div>

            {/* Additional Details */}
            <div className="rounded-card border border-line p-4">
              <h4 className="mb-3 text-sm font-medium text-ink-2">Details</h4>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {customer.avgOrderValue != null && (
                  <>
                    <dt className="text-ink-3">Avg Order Value</dt>
                    <dd className="font-medium text-ink">{formatCurrency(customer.avgOrderValue, currency)}</dd>
                  </>
                )}
                {customer.lastOrderDate && (
                  <>
                    <dt className="text-ink-3">Last Order</dt>
                    <dd className="font-medium text-ink">{formatDate(customer.lastOrderDate)}</dd>
                  </>
                )}
                {customer.lastContactDate && (
                  <>
                    <dt className="text-ink-3">Last Contact</dt>
                    <dd className="font-medium text-ink">{formatDate(customer.lastContactDate)}</dd>
                  </>
                )}
                {customer.satisfactionScore != null && (
                  <>
                    <dt className="text-ink-3">Satisfaction</dt>
                    <dd className="font-medium text-ink">{customer.satisfactionScore}/5</dd>
                  </>
                )}
              </dl>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-3 border-t border-line pt-4">
              {/* Contact actions — hidden while editing notes/tags */}
              <div className="flex items-center gap-2">
                {!editing && (
                  <>
                    <button
                      onClick={() => setIsComposeOpen(true)}
                      disabled={!customer.phoneNumber}
                      title={customer.phoneNumber ? `Message ${displayName}` : 'No phone number on file'}
                      className="flex items-center gap-1.5 rounded-pill px-3 py-2 text-sm font-medium text-brand hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      Message
                    </button>
                    {customer.phoneNumber && (
                      <CallCustomerButton
                        customerPhone={customer.phoneNumber}
                        customerName={displayName}
                      />
                    )}
                  </>
                )}
              </div>

              <div className="flex items-center gap-3">
                {editing ? (
                  <>
                    <button
                      onClick={() => {
                        setEditing(false);
                        setNotes(customer.notes || '');
                        setTags(customer.tags.join(', '));
                      }}
                      className="rounded-pill border border-line px-4 py-2 text-sm font-medium text-ink-2 hover:bg-black/[0.04]"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={updateMutation.isPending}
                      className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-ink disabled:opacity-50"
                    >
                      {updateMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setEditing(true)}
                    className="rounded-pill border border-line px-4 py-2 text-sm font-medium text-ink-2 hover:bg-black/[0.04]"
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-64 items-center justify-center text-ink-3">
            Customer not found
          </div>
        )}
      </div>
    </div>

    {/* Outbound message compose — prefilled for this customer. */}
    {customer && (
      <NewConversationModal
        key={customer.id}
        isOpen={isComposeOpen}
        onClose={() => setIsComposeOpen(false)}
        initialPhone={customer.phoneNumber}
        initialName={displayName}
        onSent={() => {
          setIsComposeOpen(false);
          onClose();
          navigate('/dashboard');
        }}
      />
    )}
    </>
  );
}

function MetricCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-card border border-line bg-surface p-3">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-ink-3">{label}</span>
      </div>
      <p className="text-lg font-semibold text-ink">{value}</p>
    </div>
  );
}
